import type { BrowserPlugin } from '@snowplow/browser-tracker';
import { User } from '@/api/types/public';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { ZL_USERINFO } from '@/utils/constants';
import { isInZeloolApp } from '@/utils/tools';
import { shouldTrackSnowplowError } from '../snowplowErrorFilter';
import { resolveSnowplowPixels } from '../snowplowPixelMapping';
import { Provider, ProviderType, TrackingEvent } from '../types';
import { createSnowplowGzipFetch } from './snowplowGzipFetch';

const TRACKER_NAMESPACE = 'sp1';
const ZEELOOL_SCHEMA = 'iglu:com.zeelool/event/jsonschema/1-0-0';
const PUBLIC_PROPS_SCHEMA = 'iglu:com.zeelool/public_properties/jsonschema/1-0-0';
const MAX_EVENT_QUEUE_SIZE = 100;

type BrowserTrackerModule = typeof import('@snowplow/browser-tracker');
type WebviewTrackerModule = typeof import('@snowplow/webview-tracker');
type ErrorTrackingPluginModule = typeof import('@snowplow/browser-plugin-error-tracking');

export interface SnowplowProviderDeps {
  loadBrowserTracker?: () => Promise<BrowserTrackerModule>;
  loadWebviewTracker?: () => Promise<WebviewTrackerModule>;
  loadErrorTrackingPlugin?: () => Promise<ErrorTrackingPluginModule>;
}

const defaultSnowplowProviderDeps: Required<SnowplowProviderDeps> = {
  loadBrowserTracker: () => import('@snowplow/browser-tracker'),
  loadWebviewTracker: () => import('@snowplow/webview-tracker'),
  loadErrorTrackingPlugin: () => import('@snowplow/browser-plugin-error-tracking'),
};

interface SnowplowConfig {
  collectorUrl: string;
  appId: string;
  platform: string;
  platformName: string;
  envName: string;
  isEnabled?: boolean;
  gzipEnabled?: boolean;
  errorTrackingEnabled?: boolean;
}

interface SnowplowUserData {
  customer_id?: User['id'];
  email?: User['email'];
  phone?: User['telephone'];
}

/**
 * 判断 Snowplow user_data 字段是否可以上报。
 */
function isValidUserDataValue(value: unknown): boolean {
  return (
    value !== '' &&
    value !== null &&
    value !== undefined &&
    value !== 'null' &&
    value !== 'undefined'
  );
}

/**
 * 读取 localStorage 中的用户信息，失败时返回空对象
 */
function getZlUserInfo(): User {
  try {
    return JSON.parse(localStorage.getItem(ZL_USERINFO) || '{}') || {};
  } catch {
    return {};
  }
}

export class SnowplowProvider implements Provider {
  type = ProviderType.SNOWPLOW;
  isEnabled: boolean;
  loggingEnabled = false;
  private initialized = false;
  private sdkReady = false;
  private sdkFailed = false;
  private loadSdkPromise: Promise<void> | null = null;
  private browserTracker: BrowserTrackerModule | null = null;
  private webviewTracker: WebviewTrackerModule | null = null;
  private eventQueue: TrackingEvent[] = [];
  private config: SnowplowConfig;
  private deps: Required<SnowplowProviderDeps>;

  constructor(config: SnowplowConfig, deps: SnowplowProviderDeps = {}) {
    this.config = config;
    this.deps = {
      loadBrowserTracker: deps.loadBrowserTracker ?? defaultSnowplowProviderDeps.loadBrowserTracker,
      loadWebviewTracker: deps.loadWebviewTracker ?? defaultSnowplowProviderDeps.loadWebviewTracker,
      loadErrorTrackingPlugin:
        deps.loadErrorTrackingPlugin ?? defaultSnowplowProviderDeps.loadErrorTrackingPlugin,
    };
    this.isEnabled = config.isEnabled ?? false;
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  async init(): Promise<boolean> {
    try {
      if (typeof window === 'undefined') return false;
      if (this.initialized) return true;

      this.initialized = true;
      void this.loadSdk();
      return true;
    } catch (error) {
      logger.error('SnowplowProvider: Failed to initialize:', error);
      return false;
    }
  }

  track(event: TrackingEvent): void {
    if (!this.initialized || typeof window === 'undefined') return;

    if (this.sdkFailed) {
      logger.warn('SnowplowProvider: SDK unavailable, dropping event:', event.eventName);
      return;
    }

    if (!this.sdkReady) {
      this.enqueueEvent(event);
      return;
    }

    this.trackReadyEvent(event);
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  enableLog(): void {
    this.loggingEnabled = true;
  }

  disableLog(): void {
    this.loggingEnabled = false;
  }

  private loadSdk(): Promise<void> {
    if (!this.loadSdkPromise) {
      this.loadSdkPromise = this.initializeSdk().catch((error) => {
        this.sdkFailed = true;
        this.eventQueue = [];
        logger.error('SnowplowProvider: Failed to load SDK:', error);
      });
    }

    return this.loadSdkPromise;
  }

  private async initializeSdk(): Promise<void> {
    if (isInZeloolApp()) {
      this.webviewTracker = await this.deps.loadWebviewTracker();
      this.sdkReady = true;
      this.flushQueuedEvents();
      return;
    }

    const browserTracker = await this.deps.loadBrowserTracker();
    const errorTrackingPlugin = await this.loadErrorTrackingPlugin();
    // 去掉尾部斜杠，避免与 postPath 拼接时产生 double-slash
    const collectorUrl = this.config.collectorUrl.replace(/\/+$/, '');

    browserTracker.newTracker(TRACKER_NAMESPACE, collectorUrl, {
      appId: this.config.appId,
      platform: 'web',
      eventMethod: 'post',
      postPath: '/zlmkt-metrics/collect',
      // gzip 封装仅作用于 browser-tracker 网络发送链路，App webview 路径不经过这里
      ...(this.config.gzipEnabled !== false ? { customFetch: createSnowplowGzipFetch(fetch) } : {}),
      plugins: [
        {
          // 公共属性作为 Global Context 附加到每条事件的 co 字段
          contexts: () => [{ schema: PUBLIC_PROPS_SCHEMA, data: this.getCommonProperties() }],
        } as BrowserPlugin,
        ...(errorTrackingPlugin ? [errorTrackingPlugin.plugin] : []),
      ],
      contexts: {
        webPage: true,
      },
    });

    this.browserTracker = browserTracker;
    if (errorTrackingPlugin) {
      this.enableErrorTracking(errorTrackingPlugin.module);
    }
    this.sdkReady = true;
    this.flushQueuedEvents();
  }

  private async loadErrorTrackingPlugin(): Promise<{
    module: ErrorTrackingPluginModule;
    plugin: BrowserPlugin;
  } | null> {
    if (!this.config.errorTrackingEnabled) {
      return null;
    }

    try {
      const pluginModule = await this.deps.loadErrorTrackingPlugin();
      return {
        module: pluginModule,
        plugin: pluginModule.ErrorTrackingPlugin(),
      };
    } catch (error) {
      logger.error('SnowplowProvider: Failed to load error tracking plugin:', error);
      return null;
    }
  }

  private enableErrorTracking(module: ErrorTrackingPluginModule): void {
    try {
      module.enableErrorTracking({ filter: shouldTrackSnowplowError });
    } catch (error) {
      logger.error('SnowplowProvider: Failed to enable error tracking:', error);
    }
  }

  private enqueueEvent(event: TrackingEvent): void {
    if (this.eventQueue.length >= MAX_EVENT_QUEUE_SIZE) {
      this.eventQueue.shift();
      logger.warn('SnowplowProvider: event queue exceeded limit, dropping oldest event');
    }

    this.eventQueue.push(event);
  }

  private flushQueuedEvents(): void {
    if (this.eventQueue.length === 0) return;

    const queuedEvents = [...this.eventQueue];
    this.eventQueue = [];
    queuedEvents.forEach((event) => this.trackReadyEvent(event));
  }

  private trackReadyEvent(event: TrackingEvent): void {
    try {
      this.dispatchEvent(event);
    } catch (error) {
      logger.error('SnowplowProvider: tracking error:', error);
    }
  }

  /**
   * 从本地用户信息中构建 Snowplow user_data 对象。
   */
  private getUserData(): SnowplowUserData {
    const userInfo = getZlUserInfo();
    const userData: SnowplowUserData = {};

    if (isValidUserDataValue(userInfo.id)) {
      userData.customer_id = userInfo.id;
    }
    if (isValidUserDataValue(userInfo.email)) {
      userData.email = userInfo.email;
    }
    if (isValidUserDataValue(userInfo.telephone)) {
      userData.phone = userInfo.telephone;
    }
    return userData;
  }

  /**
   * 构建 Snowplow 公共属性上下文，供不支持 browser global context 的链路复用。
   */
  private getPublicPropertiesContext(): Array<{ schema: string; data: Record<string, unknown> }> {
    return [{ schema: PUBLIC_PROPS_SCHEMA, data: this.getCommonProperties() }];
  }

  /**
   * 使用 Snowplow SDK 原生 page view 事件上报，确保 SPA 路由切换时刷新 page view ID。
   */
  private trackNativePageView(inApp: boolean): void {
    if (inApp) {
      if (!this.webviewTracker) {
        throw new Error('Snowplow webview tracker is not ready');
      }
      this.webviewTracker.trackPageView({ context: this.getPublicPropertiesContext() });
      return;
    }

    if (!this.browserTracker) {
      throw new Error('Snowplow browser tracker is not ready');
    }
    this.browserTracker.trackPageView();
  }

  /**
   * 统一分发方法：根据是否在 App 内路由到 webview-tracker 或 browser-tracker
   */
  private dispatchEvent(event: TrackingEvent): void {
    const inApp = isInZeloolApp();
    const eventName = event.eventName;

    if (eventName === 'page_view') {
      this.trackNativePageView(inApp);
      return;
    }

    // 公共属性与事件属性合并后一起上报，确保每条事件都有完整数据
    const commonProps = this.getCommonProperties();
    const filteredData = this.filterDollarKeys(event.properties ?? {});

    const mergedData = {
      event_name: eventName,
      properties: {
        ...commonProps, // 公共属性（与神策 registerPage 保持一致）
        ...filteredData, // 事件私有属性可覆盖公共属性同名字段
        // Snowplow 镜像神策事件，禁止输出 event_id，统一使用 sensors_event_id。
        ...(event.eventId ? { sensors_event_id: event.eventId } : {}),
      },
      user_data: this.getUserData(),
      pixel: resolveSnowplowPixels(eventName),
    };

    if (this.loggingEnabled) {
      logger.log('SnowplowProvider mergedData:', mergedData);
    }

    const sdEvent = {
      event: {
        schema: ZEELOOL_SCHEMA,
        data: mergedData,
      },
    };

    if (inApp) {
      if (!this.webviewTracker) {
        throw new Error('Snowplow webview tracker is not ready');
      }
      this.webviewTracker.trackSelfDescribingEvent(sdEvent);
      return;
    }

    if (!this.browserTracker) {
      throw new Error('Snowplow browser tracker is not ready');
    }
    this.browserTracker.trackSelfDescribingEvent(sdEvent);
  }

  /**
   * 实时读取与神策 registerCommonProperties() 对应的公共属性
   * 每次事件触发时求值，保证实时性
   */
  private getCommonProperties(): Record<string, unknown> {
    const userInfo = getZlUserInfo();
    const channelAttr = (
      window as Window &
        typeof globalThis & {
          latestChannelAttribute?: Record<string, unknown> & { expires?: number };
        }
    ).latestChannelAttribute;
    const channelValid = (channelAttr?.expires ?? 0) > Date.now() || channelAttr?.expires === -1;

    const data: Record<string, unknown> = {
      pub_platform: this.config.platform,
      pub_platform_name: this.config.platformName,
      pub_client: this.config.platform,
      pub_env_name:
        this.config.envName === 'production' ? 'production_is_nextjs' : this.config.envName,
      pub_member_level: `${(userInfo.member_level ?? -2) >= -1 ? userInfo.member_level : '9999'}`,
      pub_is_paid: !!userInfo.last_pay_time,
      pub_is_login: !!localStorage.getItem('zl-token'),
    };

    if (channelValid && channelAttr) {
      if (channelAttr.pub_zl_utm_source) data.pub_zl_utm_source = channelAttr.pub_zl_utm_source;
      if (channelAttr.pub_zl_utm_medium) data.pub_zl_utm_medium = channelAttr.pub_zl_utm_medium;
      if (channelAttr.pub_zl_utm_campaign)
        data.pub_zl_utm_campaign = channelAttr.pub_zl_utm_campaign;
      if (channelAttr.pub_zl_utm_campaignid)
        data.pub_zl_utm_campaignid = channelAttr.pub_zl_utm_campaignid;
      if (channelAttr.pub_zl_referrer) data.pub_zl_referrer = channelAttr.pub_zl_referrer;
      if (channelAttr.pub_zl_page) data.pub_zl_page = channelAttr.pub_zl_page;
    }

    if (channelAttr?.pub_landing_page) {
      data.pub_landing_page = channelAttr.pub_landing_page;
    }

    try {
      data.distinct_id = window.sensorsDataAnalytic201505?.store?.getDistinctId();
      data.anonymous_id = window.sensorsDataAnalytic201505?.quick('getAnonymousID');
    } catch (error) {
      logger.error('Failed to get distinct_id and anonymous_id:', error);
    }

    return data;
  }

  /**
   * 过滤掉 $ 开头的属性（神策内部属性，不上报给 Snowplow）
   * 同时过滤空值，与神策行为保持一致
   */
  private filterDollarKeys(obj: Record<string, unknown>): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      if (key.startsWith('$')) continue;
      if (value === '' || value === null || value === undefined) continue;
      if (value === 'null' || value === 'undefined') continue;
      if (Array.isArray(value) && value.length === 0) continue;
      result[key] = value;
    }
    return result;
  }
}
